import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { UserSingleIcon } from '@calendly/ui/components/icons';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetUserQuery } from 'common';
import { useMeetingWarning } from 'components/meetings/hooks';
import { MeetingWarningIndicator } from 'components/meetings/MeetingWarningIndicator';
import { StepThree, MI_MEETINGS_TOUR, handleCheckStepEnabled } from 'features/meeting_intelligence';
import { useScope } from 'hooks/meetings/use_scope';
import { Dot, ExternalMeetingTypeDotImg, handleGetExternalDot } from './dot';
import { FullStoryMask } from '../../../../shared/fullstory_privacy';
import { getParticipantText, EXTERNAL_SOURCES, isExternalCalendarSource } from '../../utils';
const mRNameContainerCss = "miffkun";
const MRNameContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(mRNameContainerCss, props.className)} />;
});
const nameContainerIsPhoneCss = "ns0otrl";
const nameContainerCss = "n1mjk8v1";
const NameContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(nameContainerCss, {
    [nameContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const nameExtraInfoIsPhoneCss = "n13yaw9g";
const nameExtraInfoCss = "n1b2lu3i";
const NameExtraInfo = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(nameExtraInfoCss, {
    [nameExtraInfoIsPhoneCss]: isPhone
  }, props.className)} />;
});
const ellipsedTextIsPhoneCss = "e1v36ywr";
const ellipsedTextIsBoldCss = "e1ym4qlz";
const ellipsedTextIsSmallCss = "eoqim0f";
const ellipsedTextCss = "ee86qo";
const EllipsedText = forwardRef((props, ref) => {
  const {
    isBold,
    isSmall,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(ellipsedTextCss, {
    [ellipsedTextIsPhoneCss]: isPhone,
    [ellipsedTextIsBoldCss]: isBold,
    [ellipsedTextIsSmallCss]: isSmall
  }, props.className)} />;
});
const nameIsPhoneCss = "n6dcgkr";
const nameCss = "njv0kze";
const Name = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(nameCss, {
    [nameIsPhoneCss]: isPhone
  }, props.className)} />;
});
const SoloEventDetails = ({
  event,
  hostName,
  hasSpotlight,
  showStepThree
}) => {
  const {
    eventGuestEmails: emails,
    eventType: {
      color
    },
    past: isPast,
    cancelled,
    name,
    source,
    ownerUserUuid,
    invitees,
    host
  } = event;
  const inviteeName = event?.invitee?.name;
  const {
    data: currentUser
  } = useGetUserQuery();
  const isPhone = useDeviceType('isPhone');
  const isHostMeeting = isExternalCalendarSource(source) ? host?.email === currentUser?.email : ownerUserUuid === currentUser?.uuid;
  const shouldShowExternalIcon = isPhone && source !== EXTERNAL_SOURCES.CALENDLY;
  const shouldShowDot = isPhone && source === EXTERNAL_SOURCES.CALENDLY;
  const participantText = getParticipantText({
    isHostMeeting,
    source,
    invitees,
    host,
    guestEmails: emails
  });
  const {
    hasRealtimeFeatures,
    warningKey
  } = useMeetingWarning(event);
  return <MRNameContainer>
      <NameContainer>
        <Name>
          {shouldShowExternalIcon && <ExternalMeetingTypeDotImg src={handleGetExternalDot(source)} isPast={isPast} isCancelled={cancelled} />}
          {shouldShowDot && <Dot color={color} isPast={isPast} isCancelled={cancelled} />}
          <EllipsedText isBold>
            {name}
          </EllipsedText>
        </Name>
        {showStepThree && hasSpotlight && !isPhone && <StepThree isVisible={hasSpotlight} />}
        <NameExtraInfo>
          {isPhone && <UserSingleIcon />}
          <EllipsedText isSmall>
            {participantText ? <FullStoryMask>
                {HtmlParser(participantText)}
              </FullStoryMask> : <>
                {!isPhone && hostName && <FullStoryMask>
                    {`- ${hostName} `}
                  </FullStoryMask>}
                {isPhone && hostName && <FullStoryMask>
                    {hostName}
                  </FullStoryMask>}
                <FullStoryMask>
                  {inviteeName && (emails?.length ? HtmlParser(I18n.t('publisher.meetings.link.with_and_guests', {
                name: inviteeName,
                count: emails.length
              })) : HtmlParser(I18n.t('publisher.meetings.link.with', {
                name: inviteeName
              })))}
                </FullStoryMask>
              </>}
          </EllipsedText>
        </NameExtraInfo>
      </NameContainer>
      {hasRealtimeFeatures && warningKey && <MeetingWarningIndicator warningKey={warningKey} />}
    </MRNameContainer>;
};
const GroupEventDetails = ({
  event,
  hasSpotlight,
  showStepThree,
  hostName
}) => {
  const {
    eventType: {
      color
    },
    filledCount,
    name,
    inviteesLimit,
    past: isPast,
    cancelled,
    source,
    ownerUserUuid,
    host,
    invitees
  } = event;
  const {
    data: currentUser
  } = useGetUserQuery();
  const isPhone = useDeviceType('isPhone');
  const isHostMeeting = isExternalCalendarSource(source) ? host?.email === currentUser?.email : ownerUserUuid === currentUser?.uuid;
  const shouldShowExternalIcon = isPhone && source !== EXTERNAL_SOURCES.CALENDLY;
  const shouldShowDot = isPhone && source === EXTERNAL_SOURCES.CALENDLY;
  const participantText = getParticipantText({
    isHostMeeting,
    source,
    invitees,
    host
  });
  const {
    hasRealtimeFeatures,
    warningKey
  } = useMeetingWarning(event);
  return <MRNameContainer>
      <NameContainer>
        <Name>
          {shouldShowExternalIcon && <ExternalMeetingTypeDotImg src={handleGetExternalDot(source)} isPast={isPast} isCancelled={cancelled} />}
          {shouldShowDot && <Dot color={color} isPast={isPast} isCancelled={cancelled} />}
          <EllipsedText isBold>
            {name}
          </EllipsedText>
        </Name>
        {showStepThree && hasSpotlight && !isPhone && <StepThree isVisible={hasSpotlight} />}
        <NameExtraInfo>
          {isPhone && <UserSingleIcon />}
          <EllipsedText isSmall>
            {participantText ? <FullStoryMask>
                {HtmlParser(participantText)}
              </FullStoryMask> : <>
                {!isPhone && hostName && <FullStoryMask>
                    {`- ${hostName} `}
                  </FullStoryMask>}
                {isPhone && hostName && <FullStoryMask>
                    {hostName}
                  </FullStoryMask>}
                {inviteesLimit && I18n.t('publisher.meetings.link.group.limited', {
              current: filledCount,
              count: inviteesLimit
            })}
                {!inviteesLimit && I18n.t('publisher.meetings.link.group.unlimited', {
              count: filledCount
            })}
              </>}
          </EllipsedText>
        </NameExtraInfo>
      </NameContainer>
      {hasRealtimeFeatures && warningKey && <MeetingWarningIndicator warningKey={warningKey} />}
    </MRNameContainer>;
};
export const EventDetails = ({
  event,
  hasSpotlight
}) => {
  const {
    eventType: {
      kind
    },
    isCollective,
    isCollectiveRoundRobin,
    isRoundRobin
  } = event;
  const {
    scopeType,
    scopeId
  } = useScope();
  const hasFirstMember = event?.eventMemberships?.length > 0;
  let hostName = null;
  const location = useLocation();
  const showStepThree = handleCheckStepEnabled(location, MI_MEETINGS_TOUR.STEP_THREE);
  if (hasFirstMember && !isCollective && !isCollectiveRoundRobin && !isRoundRobin && (scopeType !== 'user' || scopeId !== 'me')) {
    hostName = event.eventMemberships[0].name || event.eventMemberships[0].email;
  }
  if (kind !== 'group') {
    return <SoloEventDetails event={event} hostName={hostName} showStepThree={showStepThree} hasSpotlight={hasSpotlight} />;
  }
  return <GroupEventDetails event={event} hostName={hostName} showStepThree={showStepThree} hasSpotlight={hasSpotlight} />;
};

require("./event_details.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./event_details.js");